import React, { useState } from "react";

/**
 * PropertyConnect Adaptive Media System — shared presentation primitive (P1 foundation).
 *
 * Presents uploaded creative (portrait / square / landscape / ultrawide) without
 * distortion or needless cropping, inside controlled, bounded geometry. It does NOT
 * render platform text/CTAs over the artwork — callers render classification metadata
 * OUTSIDE/below the frame (protected-media rule).
 *
 * Intrinsic dimensions are honored when passed (future: persisted width/height), else
 * we fall back to the browser's naturalWidth/naturalHeight on load (no extra request).
 */

export const ORIENTATION = {
  PORTRAIT: "PORTRAIT",
  SQUARE_OR_STANDARD: "SQUARE_OR_STANDARD",
  LANDSCAPE: "LANDSCAPE",
  ULTRAWIDE: "ULTRAWIDE",
};

// Audited thresholds (ratio = width / height).
export function classifyOrientation(w, h) {
  if (!w || !h) return null;
  const r = w / h;
  if (r < 0.8) return ORIENTATION.PORTRAIT;
  if (r <= 1.35) return ORIENTATION.SQUARE_OR_STANDARD;
  if (r <= 2.0) return ORIENTATION.LANDSCAPE;
  return ORIENTATION.ULTRAWIDE;
}

// Policy/context distinction (reusable by Events and future media surfaces):
//  - MEDIA_CONTEXT.CARD   → DISCOVERY/CARD POLICY: controlled adaptive geometry (bounded bands).
//  - MEDIA_CONTEXT.DETAIL → DETAIL POLICY: intrinsic-orientation-aware geometry with sane min/max.
export const MEDIA_CONTEXT = { CARD: "card", DETAIL: "detail" };

// DISCOVERY/CARD POLICY — controlled orientation bands keep a premium grid rhythm
// (portrait may be taller than landscape, but the spread is constrained; no masonry).
// Landscape and ultrawide intentionally share one band so wide creatives don't collapse
// into microscopic strips and rows stay visually even. Each value is width / height.
const CARD_BAND = {
  PORTRAIT: 4 / 5,            // 0.80 (tallest band)
  SQUARE_OR_STANDARD: 1,     // 1.00
  LANDSCAPE: 3 / 2,          // 1.50 (shortest band)
  ULTRAWIDE: 3 / 2,          // 1.50 (merged with landscape for grid rhythm)
};

// Neutral default band before dimensions are known (minimises reflow for mixed content).
const DEFAULT_CARD_ORIENTATION = ORIENTATION.SQUARE_OR_STANDARD;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// PROPERTY policy (media-semantic, deterministic — no AI/classification):
// Property surfaces keep a FIXED frame (grid/stage consistency) and choose fit by the
// only safe signal available from existing data — intrinsic orientation:
//   • LANDSCAPE / SQUARE_OR_STANDARD  → object-cover (immersive photography, honors focal)
//   • PORTRAIT / ULTRAWIDE            → object-contain (protect complete asset: floor plans,
//     price/computation sheets, maps, tall marketing posters, panoramas) + branded backdrop.
// NOTE: dimensions alone cannot distinguish a landscape floor-plan from a landscape photo;
// a future additive `media_class` field would be required for that (see audit).
export function propertyFit(w, h) {
  const o = classifyOrientation(w, h);
  if (o === ORIENTATION.PORTRAIT || o === ORIENTATION.ULTRAWIDE) return "contain";
  return "cover";
}

// P5: asset-level media class → fit policy. Non-photographic assets (floor plans, maps,
// price/computation sheets, brochures) always contain (never crop information). PHOTO uses
// orientation-based propertyFit. Lightbox/fullscreen always contain regardless (caller-enforced).
export const PROPERTY_MEDIA_CLASSES = ["PHOTO", "MARKETING_CREATIVE", "FLOOR_PLAN", "MAP", "PRICE_SHEET", "BROCHURE"];
export const MEDIA_CLASS_LABEL = { PHOTO: "Photo", MARKETING_CREATIVE: "Marketing poster", FLOOR_PLAN: "Floor plan", MAP: "Site/Location map", PRICE_SHEET: "Price/Computation", BROCHURE: "Brochure/Marketing" };
export function classFit(mediaClass, w, h) {
  if (mediaClass && mediaClass !== "PHOTO") return "contain";
  return propertyFit(w, h);
}

export function AdaptiveMediaFrame({
  src,
  thumbSrc,
  alt = "",
  intrinsic = null,          // { w, h } when persisted (future); else runtime fallback is used
  mediaType = "image",       // future extensibility (e.g. "video")
  policy = "preservation",   // "preservation" (contain + backdrop) | "cover" (crop + focal)
  context = "card",          // "card" (fixed bands) | "detail" (geometry follows creative)
  backdrop = true,           // blurred duplicate behind contained media
  tone = "dark",             // "dark" (navy — ads/protected) | "light" (slate — artwork surfaces)
  focal = "center",          // object-position for cover policy
  rounded = "rounded-2xl",
  className = "",
  style,
  loading = "lazy",
  fallback = null,           // node shown when no src / load error
  onError,                   // optional passthrough (caller may swap to its own fallback)
  maxHeight,                 // optional px cap for DETAIL geometry (carousel/hero contexts)
  stageHeight,               // optional fixed protected-stage height (CSS value) — even-rhythm
                             // discovery placements: creative is contained+centered within it
  naturalCard = false,       // card context: frame takes the creative's NATURAL aspect ratio
                             // (edge-to-edge, no band, no letterbox); falls back to bands on
                             // unknown dims / missing cover (opt-in; ads/spotlight unaffected)
  "data-testid": testid,
}) {
  const [nat, setNat] = useState(null);
  const [errored, setErrored] = useState(false);

  const dims = intrinsic && intrinsic.w && intrinsic.h ? intrinsic : nat;
  const orientation = dims ? classifyOrientation(dims.w, dims.h) : null;
  const ratio = dims ? dims.w / dims.h : null;

  const onLoad = (e) => {
    if (intrinsic && intrinsic.w && intrinsic.h) return; // persisted dims win — no runtime work
    const w = e.target?.naturalWidth;
    const h = e.target?.naturalHeight;
    if (w && h && (!nat || nat.w !== w || nat.h !== h)) setNat({ w, h });
  };

  const handleError = (e) => {
    setErrored(true);
    if (onError) onError(e);
  };

  const isCover = policy === "cover";
  const isDetail = context === "detail";

  // ---- container geometry ----
  let containerStyle = {};
  if (stageHeight) {
    // FIXED PROTECTED STAGE — placement policy caps page space; source ratio only
    // governs how the artwork is contained/centered within the stage (even card rhythm).
    containerStyle = { width: "100%", height: stageHeight };
  } else if (isCover) {
    containerStyle = { aspectRatio: String(isDetail ? 16 / 10 : 4 / 3) };
  } else if (!isDetail) {
    if (naturalCard && dims && ratio && src && !errored) {
      containerStyle = { aspectRatio: String(ratio) }; // whole image, edge-to-edge, no letterbox
    } else {
      const band = CARD_BAND[orientation || DEFAULT_CARD_ORIENTATION];
      containerStyle = { aspectRatio: String(band) };
    }
  } else {
    // DETAIL POLICY — geometry follows the creative's intrinsic orientation, clamped to
    // sensible min/max so extreme uploads never dominate or overwhelm the page.
    const o = orientation || ORIENTATION.LANDSCAPE;
    const r = ratio ? clamp(ratio, 0.5, 3.2) : 4 / 3;
    const mh = maxHeight || null; // carousel/hero contexts pass a tighter cap
    if (o === ORIENTATION.PORTRAIT || o === ORIENTATION.SQUARE_OR_STANDARD) {
      // narrower/taller, centered; height-driven so width = height * ratio.
      containerStyle = { aspectRatio: String(r), height: mh ? `min(72vw, ${mh}px)` : "clamp(260px, 72vw, 600px)", maxWidth: "100%" };
    } else if (o === ORIENTATION.LANDSCAPE) {
      // wider/shallower, full width; height = width / ratio, capped.
      containerStyle = { aspectRatio: String(r), width: "100%", maxHeight: `${mh || 480}px` };
    } else {
      // ULTRAWIDE — wide but sensibly capped so banners do not dominate the page.
      containerStyle = { aspectRatio: String(r), width: "min(100%, 820px)", maxHeight: `${mh ? Math.round(mh * 0.8) : 300}px` };
    }
  }

  const showFallback = !src || errored;
  const naturalFit = naturalCard && dims && ratio && !isCover && !isDetail && !stageHeight && !showFallback;
  const lightTone = tone === "light";
  const toneBg = lightTone ? "bg-slate-100" : "bg-gradient-to-br from-[#0F2C59] to-[#16407f]";

  const mediaBox = (
    <div
      data-testid={testid}
      className={`relative overflow-hidden ${toneBg} ${rounded} ${isDetail ? "" : "w-full"} ${className}`}
      style={{ ...containerStyle, ...style }}
    >
      {showFallback ? (
        fallback
      ) : (
        <>
          {backdrop && !isCover && !naturalFit && (
            <img
              src={thumbSrc || src}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover scale-110 blur-2xl pointer-events-none ${lightTone ? "opacity-20" : "opacity-40"}`}
            />
          )}
          <img
            src={src}
            alt={alt}
            onLoad={onLoad}
            onError={handleError}
            loading={loading}
            decoding="async"
            style={isCover ? { objectPosition: focal } : undefined}
            className={`relative w-full h-full ${isCover || naturalFit ? "object-cover" : "object-contain"}`}
          />
        </>
      )}
    </div>
  );

  // Detail boxes are height-driven for portrait/square, so center them in the row.
  if (isDetail) return <div className="flex justify-center">{mediaBox}</div>;
  return mediaBox;
}

export default AdaptiveMediaFrame;

// ---------------------------------------------------------------------------
// PropertyCover — shared property cover primitive (public + authenticated).
// PURE IMAGE FRAME: it renders ONLY the artwork, never UI. No badges, Save/Share,
// scrims or overlays are ever drawn on top of the creative (callers render all controls
// and badges OUTSIDE the frame, in the card's content strip below the image).
// DEFAULT = NATURAL ASPECT: the frame takes the image's own width/height ratio so the
// ENTIRE image is shown edge-to-edge — no letterboxing, no pillarboxing, no crop, no
// stretch. Frame heights therefore vary per upload (no forced 4:3 box). The initial ratio
// comes from the persisted cover_width/cover_height (zero layout shift), falling back to the
// image's natural dimensions on load. "Fill Frame" (object-cover + focal/zoom on a fixed 4:3)
// is used ONLY when the owner explicitly saved cover_presentation.mode === "FILL"; an explicit
// baked FIT variant (pre-composed 4:3) is shown when present.
// NOTE: callers passing aspect={null} opt out entirely and control geometry via their parent.
// ---------------------------------------------------------------------------
export function PropertyCover({ listing = {}, className = "", imgClassName = "", aspect = "4/3", rounded = "", hoverZoom = false, desktopCover = false }) {
  const pres = listing.cover_presentation;
  const [nat, setNat] = useState(null); // runtime natural dims (legacy listings w/o cover_width)
  let fill = false, src = listing.image, objPos = "center", transform;
  if (pres?.mode === "FILL") {
    fill = true;
    if (pres.focal) objPos = `${(pres.focal.x ?? 0.5) * 100}% ${(pres.focal.y ?? 0.5) * 100}%`;
    if (pres.zoom && pres.zoom > 1) transform = `scale(${pres.zoom})`;
  } else if (pres?.mode === "FIT" && pres.variant_url) {
    fill = true; src = pres.variant_url; // baked, pre-composed 4:3 (explicit owner choice)
  }

  // DEFAULT (no explicit FILL/FIT) with a requested aspect → render at the image's NATURAL ratio.
  const useNatural = !fill && !!aspect;
  const dims = (listing.cover_width && listing.cover_height)
    ? { w: listing.cover_width, h: listing.cover_height }
    : nat;
  const naturalAspect = dims ? `${dims.w} / ${dims.h}` : aspect; // fall back to requested until known

  const containerAspect = fill ? aspect : useNatural ? naturalAspect : aspect;
  const showBackdrop = !fill && !useNatural && src; // blurred fill only for legacy letterboxed contain

  const onLoad = (e) => {
    if (listing.cover_width && listing.cover_height) return; // persisted dims win
    const w = e.target?.naturalWidth, h = e.target?.naturalHeight;
    if (w && h && (!nat || nat.w !== w || nat.h !== h)) setNat({ w, h });
  };

  // DESKTOP-ONLY (>=1024px) consistent 4:3 cover frame (opt-in; mobile/tablet unchanged).
  // Marketplace/My-Properties cards request this; all other PropertyCover callers keep defaults.
  if (desktopCover) {
    const desktopSrc = pres?.mode === "FIT" ? listing.image : src; // avoid baked FIT padding → no internal gutters
    const desktopObjPos = pres?.mode === "FILL" ? objPos : "center"; // honor saved focal, else centred crop
    const desktopTransform = pres?.mode === "FILL" ? transform : undefined;
    return (
      <div className={`relative ${className}`}>
        {/* < 1024px: existing presentation preserved exactly */}
        <div className={`lg:hidden relative overflow-hidden ${fill ? "bg-[#0F2C59]" : "bg-slate-100"} ${rounded}`} style={containerAspect ? { aspectRatio: containerAspect } : undefined}>
          {showBackdrop && <img src={src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-20 pointer-events-none" />}
          <img src={src} alt={listing.title || ""} loading="lazy" onLoad={onLoad} className={`relative w-full h-full ${fill || useNatural ? "object-cover" : "object-contain"} ${hoverZoom && fill ? "transition-transform duration-500 group-hover:scale-[1.04]" : ""} ${imgClassName}`} style={fill ? { objectPosition: objPos, transform } : undefined} />
        </div>
        {/* >= 1024px: consistent 4:3 cover (space reserved before load → no layout jump) */}
        <div className={`hidden lg:block relative overflow-hidden bg-slate-100 ${rounded}`} style={{ aspectRatio: "4 / 3" }}>
          <img src={desktopSrc} alt={listing.title || ""} loading="lazy" className={`w-full h-full object-cover ${hoverZoom ? "transition-transform duration-500 group-hover:scale-[1.04]" : ""} ${imgClassName}`} style={{ objectPosition: desktopObjPos, transform: desktopTransform }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${fill ? "bg-[#0F2C59]" : "bg-slate-100"} ${rounded} ${className}`} style={containerAspect ? { aspectRatio: containerAspect } : undefined}>
      {showBackdrop && (
        <img src={src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-20 pointer-events-none" />
      )}
      <img
        src={src}
        alt={listing.title || ""}
        loading="lazy"
        onLoad={onLoad}
        className={`relative w-full h-full ${fill || useNatural ? "object-cover" : "object-contain"} ${hoverZoom && fill ? "transition-transform duration-500 group-hover:scale-[1.04]" : ""} ${imgClassName}`}
        style={fill ? { objectPosition: objPos, transform } : undefined}
      />
    </div>
  );
}
